import { React, useId, useState } from "react";
import sendServerLogging from "../../../utils/sendServerLogging";
import userJSON from "../../../utils/userJSON";

export default function KnowtexEMSuggestion({
  setIsRationaleProvided,
  userRationale,
  setUserRationale,
  showRationale,
  setShowRationale,
  mdm_suggested,
  setFinalSelectedLevel,
  setFinalSelectedTs,
  levels,
  patient,
  emData,
  useTimeBasedCoding,
  timeBasedLevel,
  org,
}) {
  const rationaleId = useId();
  const [activeButton, setActiveButton] = useState(null);

  // Get selection basis information
  const getSelectionBasis = () => {
    // If we're using time-based coding due to higher time level, show that
    if (useTimeBasedCoding) {
      return "Based on Time Spent";
    }

    // Otherwise use the original emData selection basis
    if (!emData?.selection_on) return "";

    switch (emData.selection_on) {
      case "mdm":
        return "Based on Medical Decision Making";
      case "time":
        return "Based on Total Encounter Time";
      default:
        return "";
    }
  };

  // Extract recommended level from E&M code
  const getRecommendedLevel = (eAndMCode) => {
    if (!eAndMCode) return null;
    const lastDigit = eAndMCode.toString().slice(-1);
    return parseInt(lastDigit);
  };
  const emRecommendedLevel = getRecommendedLevel(emData?.e_and_m_code);

  const typeLabels = {
    // Complexity
    CHRONIC_STABLE: "Chronic, Stable",
    ACUTE_UNCOMPLICATED: "Acute, Uncomplicated",
    CHRONIC_PROGRESSION: "Chronic, Progression",
    ACUTE_COMPLICATED: "Acute, Complicated",

    // Data (example)
    ORDER_TEST: "Order Test",
    REVIEW_RESULT: "Review Test Result",
    EXTERNAL_DISCUSSION: "External Discussion",
    HISTORIAN: "Historian",
  };

  // This is the second row of level buttons
  const handleClick = (selectedLevel) => {
    // server logging
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "E&M_SUGGESTED_CODE_CLICKED",
        eventData: { mrn: patient.mrn, em_code: selectedLevel },
        patientId: patient.patient_id,
      },
    });
    const timestampInSeconds = Math.floor(Date.now() / 1000);

    setActiveButton(selectedLevel);
    setFinalSelectedLevel(selectedLevel);
    setFinalSelectedTs(timestampInSeconds);

    // Compare against the current effective level (time-based or MDM-based)
    const currentEffectiveLevel = useTimeBasedCoding ? timeBasedLevel : levels.note;

    if (parseInt(selectedLevel) === parseInt(currentEffectiveLevel)) {
      // Matches recommended => no rationale needed
      setShowRationale(false);
      setIsRationaleProvided(true);
    } else {
      // Differs from recommended => require rationale
      setShowRationale(true);
      // If they have *already typed* something, we consider it provided
      setIsRationaleProvided(userRationale.trim().length > 0);
    }
  };

  //   fcn to handle checking if rationale is provided.
  const handleRationaleChange = (e) => {
    const text = e.target.value;
    setUserRationale(text);
    setIsRationaleProvided(text.trim().length > 0);
  };

  return (
    <div className="flex flex-col mt-3">
      {/* Show recommendation info, etc. */}
      <h3 className="text-accent1 text-sm font-bold italic mb-2">
        Knowtex's Recommended Level of Service and Rationale:
      </h3>
      <div className="flex w-fit bg-transparent-green rounded-xl mb-3 px-3 py-1">
        <h3 className="text-ktx-green text-md font-black">
          Level: {useTimeBasedCoding ? timeBasedLevel : levels.note}
        </h3>
      </div>
      {(emData?.e_and_m_code || useTimeBasedCoding) && org !== "VA" && (
        <h3 className="text-light-grey text-sm font-medium mb-2">
          Recommended: {useTimeBasedCoding ?
            `${emData?.patient_type === "new" ? "9920" + timeBasedLevel : "9921" + timeBasedLevel}` :
            emData.e_and_m_code
          } (Level {useTimeBasedCoding ? timeBasedLevel : emRecommendedLevel})
        </h3>
      )}
      {getSelectionBasis() && (
        <h4 className="text-light-grey text-xs italic mb-2">
          {getSelectionBasis()}
        </h4>
      )}

      {/* rational cards - hide when using time-based coding */}
      {!useTimeBasedCoding && (
        <div className="flex flex-row md:flex-row gap-6 w-full">
          {/* Complexity Card */}
          <div className="flex-1 border-l-4 border-transparent-purple shadow-knowtex-shadow rounded-l-none p-6 flex flex-col">
            <div className="flex flex-row justify-center items-center">
              <h3 className="text-accent1 text-md font-bold mb-2 text-center mr-1">
                Complexity
              </h3>
              <h3 className="text-ktx-green text-xs font-semibold mb-2 text-center w-fit bg-transparent-green rounded-xl px-2 py-1 ml-1">
                Level : {levels.complexity}
              </h3>
            </div>
            {emData?.mdm?.complexity?.problems?.length !== 0 ? (
              <div className="flex flex-col">
                {emData.mdm.complexity.problems.map((problem, index) => {
                  // Use our mapping to get a friendly label
                  const displayComplexityType =
                    typeLabels[problem.type] || problem.type;

                  return (
                    <div
                      key={index}
                      className="bg-white border rounded-md p-2 mb-2 shadow-sm"
                    >
                      {/* top label: problem type */}
                      <div className="flex flex-row items-center text-xs text-gray-500 font-semibold">
                        <span>{displayComplexityType}</span>
                      </div>

                      {/* bold title */}
                      <p className="text-black text-sm font-bold mt-2">
                        {problem.meta.name}
                      </p>

                      {/* details */}
                      <p className="text-xs text-gray-600 mt-1">
                        {problem.meta.details}
                      </p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex">
                <p className="text-knowtex-grey whitespace-pre-line text-xs font-semibold mt-">
                  No complexity data to display.
                </p>
              </div>
            )}
          </div>

          {/* Risk Card */}
          <div className="flex-1 border-l-4 border-transparent-purple shadow-knowtex-shadow rounded-l-none p-6 flex flex-col">
            <div className="flex flex-row justify-center items-center">
              <h3 className="text-accent1 text-md font-bold mb-2 text-center mr-1">
                Risk
              </h3>
              <h3 className="text-ktx-green text-xs font-semibold mb-2 text-center w-fit bg-transparent-green rounded-xl px-2 py-1 ml-1">
                Level : {levels.risk}
              </h3>
            </div>
            <div className="bg-white border rounded-md p-2 mb-2 shadow-sm">
              {/* top label (risk type) */}
              <div className="flex flex-row items-center text-xs text-gray-500 font-semibold">
                <span>{emData.mdm.risk.type}</span>
              </div>

              {/* bold title or heading for the rationale */}
              <p className="text-black text-sm font-bold mt-2">Risk Rationale</p>

              {/* subtext / rationale */}
              <p className="text-xs text-gray-600 mt-1">
                {emData.mdm.risk.meta.rationale}
              </p>
            </div>
          </div>

          {/* Data Card */}
          <div className="flex-1 border-l-4 border-transparent-purple shadow-knowtex-shadow rounded-l-none p-6 flex flex-col">
            <div className="flex flex-row justify-center items-center">
              <h3 className="text-accent1 text-md font-bold mb-2 text-center mr-1">
                Data
              </h3>
              <h3 className="text-ktx-green text-xs font-semibold mb-2 text-center w-fit bg-transparent-green rounded-xl px-2 py-1 ml-1">
                Level : {levels.data}
              </h3>
            </div>

            {emData.mdm.data.items.length !== 0 ? (
              <div className="flex flex-col">
                {emData.mdm.data.items.map((item, index) => {
                  const displayType = typeLabels[item.type] || item.type; // fallback if not in the map

                  return (
                    <div
                      key={index}
                      className="bg-white border rounded-md p-2 mb-2 shadow-sm"
                    >
                      {/* top label with item type */}
                      <div className="flex flex-row items-center text-xs text-gray-500 font-semibold">
                        <span>{displayType}</span>
                      </div>

                      {/* bold title */}
                      <p className="text-black text-sm font-bold mt-2">
                        {item.meta.name}
                      </p>

                      {/* subtext / description */}
                      <p className="text-xs text-gray-600 mt-1">
                        {item.meta.context}
                      </p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex">
                <p className="text-knowtex-grey whitespace-pre-line text-xs font-semibold">
                  No data to display.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Final Selection row */}
      <div className="flex flex-col mt-3">
        <h3 className="text-accent1 text-sm font-semibold mb-2">
          Final Selection:
        </h3>
        <div
          className="flex flex-row gap-4"
          role="group"
          aria-label="Final level of service selection"
        >
          {[2, 3, 4, 5].map((number) => (
            <button
              key={number}
              type="button"
              aria-label={`Final level of service ${number}`}
              aria-pressed={activeButton === number}
              onClick={() => {
                handleClick(number);
                // server logging
                sendServerLogging({
                  body: {
                    ...userJSON(localStorage.getItem("userName")),
                    event: "E&M_FINAL_CODE_CLICKED",
                    eventData: { mrn: patient.mrn, em_code: number },
                    patientId: patient.patient_id,
                  },
                });
              }}
              className={`border-accent1 border rounded-md hover:bg-transparent-purple 
             hover:text-accent1 text-accent1 font-bold py-2 px-4 w-12 h-12 
             flex items-center justify-center ${activeButton === number ? "bg-transparent-purple" : ""
                }`}
            >
              {number}
            </button>
          ))}
        </div>
      </div>

      {/* If final level != recommended, show rationale*/}
      {showRationale && (
        <div>
          <label
            htmlFor={rationaleId}
            className="text-accent1 text-sm font-semibold mb-2 mt-2"
          >
            Please enter your rationale below:
          </label>
          <p id={`${rationaleId}-hint`} className="text-light-grey italic text-sm">
            *Required
          </p>
          <textarea
            id={rationaleId}
            aria-describedby={`${rationaleId}-hint`}
            className="form-control resize-none rounded-md border-0 shadow-knowtex-shadow 
    text-accent1 bg-secondary-grey"
            onChange={handleRationaleChange}
            value={userRationale}
            required
          />
        </div>
      )}
    </div>
  );
}